/**
 * @fileoverview <LegalDocumentPage> — chrome-agnostic legal document
 * renderer.
 *
 * Composers wrap this component in either:
 *   - tenant chrome via `<TenantInfoPage>` from
 *     `@puffin-core/page-shell` (inside `<AdminPageShell>`), or
 *   - marketing chrome via the per-portal `<MarketingLegalPage>`
 *     wrapper (which composes `<DocumentHead>` + `<SchemaMarkup>` +
 *     `Header` + `Footer`).
 *
 * The component itself ships only the inner prose: heading, last-
 * updated badge, anchored TOC, and one `<section>` per registered
 * `LegalSection`. Frontmatter token substitution is applied at render
 * time via the `interpolate()` utility.
 *
 * Two languages meet on this page, and each part says which it is in (WCAG 3.1.2). The DOCUMENT is the
 * registry's, in `LEGAL_DOCUMENT_LANGUAGE`, and its root carries that `lang`. The CHROME a reader
 * navigates by — the unavailable notice, the table of contents, the effective-date line, the contact
 * block — is worded from the package's `legalContent` namespace (#56, PACKAGE-I18N-SEAT U3), and every
 * element worded from it carries the bundle's own `chromeLanguage`. Each chrome part is its own small
 * component, so the page component itself calls no hook beyond `useLegalDocument`.
 *
 * @module @puffin-core/legal-content/components/LegalDocumentPage
 */

import { useIntlLocale } from '@puffin-core/i18n-bootstrap';
import PropTypes from 'prop-types';
import { copyrightYear } from '@puffin-core/temporal-core/copyright-year';
import { useLegalDocument } from '../hooks/useLegalDocument.js';
import { useLegalContentTranslate } from '../hooks/useLegalContentTranslate.js';
import { LEGAL_DOCUMENT_LANGUAGE } from '../registry/index.js';
import { interpolate } from '../registry/interpolate.js';
import { SubprocessorList } from './SubprocessorList.jsx';

/**
 * @param {Object} props
 * @param {string} props.appKey
 * @param {string} props.documentKey
 * @param {boolean} [props.tocEnabled=true] — render the in-page TOC.
 * @param {boolean} [props.showHeader=true] — render the inner h1 + last-
 *   updated badge. Set `false` when the consumer chrome (e.g.
 *   `<TenantInfoPage>` in `@puffin-core/page-shell`) already renders a
 *   page-level h1, so the document doesn't double-up the heading.
 * @returns {React.JSX.Element}
 */
export function LegalDocumentPage({ appKey, documentKey, tocEnabled = true, showHeader = true }) {
  const { document: doc, error } = useLegalDocument({ appKey, documentKey });

  if (error || !doc) {
    /*
     * Never render the internal error into the page.
     *
     * `error.message` is a developer diagnostic — for an unregistered app it reads
     * `getLegalDocument: unknown appKey "prospect" (registered: parcel, planner, palm, …)`,
     * enumerating every app in the registry. That was being printed verbatim to whoever opened
     * /privacy, and since MSSG-001 prerenders these routes it was also being BAKED INTO STATIC
     * HTML and served to crawlers as the page's entire content.
     *
     * The diagnostic still reaches developers (below); what the visitor sees is an honest
     * neutral message that does not leak the registry.
     */
    if (error) {
      console.error('[legal-content] document unavailable:', error.message);
    }

    return <LegalDocumentUnavailable showHeader={showHeader} />;
  }

  const { frontmatter, sections } = doc;

  return (
    <div className="legal-document" lang={LEGAL_DOCUMENT_LANGUAGE} data-testid="legal-document">
      {showHeader ? <LegalDocumentHeader frontmatter={frontmatter} /> : null}

      {tocEnabled ? <LegalDocumentToc sections={sections} /> : null}

      <div className="legal-document-content">
        {sections.map((section) => (
          <SectionRenderer key={section.id} section={section} frontmatter={frontmatter} />
        ))}
      </div>

      <footer className="legal-document-footer">
        <p>
          {frontmatter.productName} · © {copyrightYear()} {frontmatter.legalEntity}
        </p>
      </footer>
    </div>
  );
}

LegalDocumentPage.propTypes = {
  appKey: PropTypes.string,
  documentKey: PropTypes.string,
  tocEnabled: PropTypes.bool,
  showHeader: PropTypes.bool,
};

/**
 * The notice a visitor reads when no document can be shown. Every word in it is chrome, so the whole
 * notice carries the language its words are in — not the document's, which it does not contain.
 *
 * @param {Object} props
 * @param {boolean} props.showHeader - Render the notice's own `<h1>`.
 * @returns {React.JSX.Element}
 */
function LegalDocumentUnavailable({ showHeader }) {
  const t = useLegalContentTranslate();
  return (
    <div
      className="legal-document"
      lang={t('chromeLanguage')}
      role="alert"
      data-testid="legal-document-error"
    >
      {showHeader ? <h1 className="legal-document-title">{t('unavailableTitle')}</h1> : null}
      <p className="legal-text">{t('unavailableMessage')}</p>
    </div>
  );
}

LegalDocumentUnavailable.propTypes = {
  showHeader: PropTypes.bool.isRequired,
};

/**
 * The in-page table of contents. Its name and heading are chrome; its links are the document's section
 * titles, so the list keeps the document's language inside the chrome's.
 *
 * @param {Object} props
 * @param {Array<{id: string, title: string}>} props.sections - The document's sections, in order.
 * @returns {React.JSX.Element}
 */
function LegalDocumentToc({ sections }) {
  const t = useLegalContentTranslate();
  return (
    <nav
      className="legal-document-toc"
      lang={t('chromeLanguage')}
      aria-label={t('tableOfContents')}
    >
      <p className="legal-document-toc-heading">{t('tableOfContents')}</p>
      <ul className="legal-document-toc-list" lang={LEGAL_DOCUMENT_LANGUAGE}>
        {sections.map((section) => (
          <li key={section.id}>
            <a className="legal-document-toc-link" href={`#${section.id}`}>
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

LegalDocumentToc.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, title: PropTypes.string.isRequired }),
  ).isRequired,
};

/**
 * Renders one section: `<section><h2>title</h2>{blocks}</section>`.
 */
function SectionRenderer({ section, frontmatter }) {
  return (
    <section
      id={section.id}
      className="legal-document-section legal-section"
      aria-labelledby={`section-heading-${section.id}`}
    >
      <h2 id={`section-heading-${section.id}`} className="legal-section-title">
        {section.title}
      </h2>
      <BlocksRenderer blocks={section.blocks} frontmatter={frontmatter} />
    </section>
  );
}

SectionRenderer.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    blocks: PropTypes.array.isRequired,
  }).isRequired,
  frontmatter: PropTypes.object.isRequired,
};

/**
 * Renders an array of blocks; used by both top-level sections and
 * nested subsection blocks.
 */
function BlocksRenderer({ blocks, frontmatter }) {
  return (
    <>
      {blocks.map((block, idx) => (
        <BlockRenderer key={`${block.type}-${idx}`} block={block} frontmatter={frontmatter} />
      ))}
    </>
  );
}

BlocksRenderer.propTypes = {
  blocks: PropTypes.array.isRequired,
  frontmatter: PropTypes.object.isRequired,
};

function BlockRenderer({ block, frontmatter }) {
  if (block.type === 'paragraph') {
    return <p className="legal-text">{interpolate(block.text, frontmatter)}</p>;
  }
  if (block.type === 'list') {
    return (
      <ul className="legal-list">
        {block.items.map((item, idx) => (
          <ListItemRenderer key={idx} item={item} frontmatter={frontmatter} />
        ))}
      </ul>
    );
  }
  if (block.type === 'subsection') {
    return (
      <div className="legal-subsection">
        <h3 className="legal-subsection-title">{block.title}</h3>
        <BlocksRenderer blocks={block.blocks} frontmatter={frontmatter} />
      </div>
    );
  }
  if (block.type === 'contact') {
    return <ContactBlock frontmatter={frontmatter} />;
  }
  // 'subprocessor-list' is the only remaining valid type per the schema
  // validator (`BLOCK_TYPES = [paragraph, list, subsection, contact,
  // subprocessor-list]`); registry data is validated end-to-end so an
  // unknown type can't reach this point in production.
  return <SubprocessorList entries={block.entries} />;
}

BlockRenderer.propTypes = {
  block: PropTypes.shape({ type: PropTypes.string.isRequired }).isRequired,
  frontmatter: PropTypes.object.isRequired,
};

/**
 * A document's `contact` block. Its group name and the email label are chrome; the product name and the
 * address are proper nouns, so the whole block carries the chrome's language.
 *
 * @param {Object} props
 * @param {{productName: string, contactEmail: string}} props.frontmatter
 * @returns {React.JSX.Element}
 */
function ContactBlock({ frontmatter }) {
  const t = useLegalContentTranslate();
  return (
    <address
      className="legal-contact"
      role="group"
      lang={t('chromeLanguage')}
      aria-label={t('contactInformation')}
    >
      <p>
        <strong>{frontmatter.productName}</strong>
      </p>
      <p>
        {t('contactEmail')}{' '}
        <a href={`mailto:${frontmatter.contactEmail}`}>{frontmatter.contactEmail}</a>
      </p>
    </address>
  );
}

ContactBlock.propTypes = {
  frontmatter: PropTypes.shape({
    productName: PropTypes.string,
    contactEmail: PropTypes.string,
  }).isRequired,
};

function ListItemRenderer({ item, frontmatter }) {
  if (typeof item === 'string') {
    return <li className="legal-list-item">{interpolate(item, frontmatter)}</li>;
  }
  return (
    <li className="legal-list-item">
      <strong>{item.strong}:</strong> {interpolate(item.text, frontmatter)}
    </li>
  );
}

ListItemRenderer.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      strong: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ]).isRequired,
  frontmatter: PropTypes.object.isRequired,
};

/**
 * Format an ISO date as a long-form English date for display.
 * All effectiveDate values in the registry are real, validated ISO dates,
 * so parsing always succeeds here.
 */
/**
 * The document header — title plus the localized effective date.
 *
 * ES-I18N-FOLLOW-ON (2026-08-02): extracted so `useIntlLocale()` lives BESIDE the date it
 * formats rather than on the page component. That is better placement on its own merits, and
 * it fixes a real failure: `LegalDocumentPage`'s null-document suite does
 * `jest.resetModules()` + a fresh `require`, which hands the freshly-required module tree a
 * SECOND React binding — so a hook on the page threw `resolveDispatcher` before the fallback
 * could render. Since PACKAGE-I18N-SEAT U3 the null-document notice is worded from the catalog, so it
 * takes a hook of its own (`<LegalDocumentUnavailable>`); that suite now renders through a
 * freshly-required `@testing-library/react`, so the renderer and the component share one React.
 *
 * The title is the document's; the effective-date line is chrome, worded and dated in the reader's
 * language, and marked with it.
 *
 * @param {object} props
 * @param {object} props.frontmatter - the document's frontmatter (title + effectiveDate)
 * @returns {React.JSX.Element}
 */
function LegalDocumentHeader({ frontmatter }) {
  const locale = useIntlLocale();
  const t = useLegalContentTranslate();
  return (
    <header className="legal-document-header">
      <h1 className="legal-document-title">{frontmatter.title}</h1>
      <p className="legal-document-effective-date" lang={t('chromeLanguage')}>
        {t('effectiveDate', { date: formatEffectiveDate(frontmatter.effectiveDate, locale) })}
      </p>
    </header>
  );
}

LegalDocumentHeader.propTypes = {
  frontmatter: PropTypes.shape({
    title: PropTypes.string,
    effectiveDate: PropTypes.string,
  }).isRequired,
};

function formatEffectiveDate(iso, locale) {
  const date = new Date(`${iso}T00:00:00Z`);
  // ES-I18N-FOLLOW-ON (2026-08-02): the locale was hardcoded `'en-US'`. `timeZone: 'UTC'`
  // stays — the input is a civil date pinned to UTC midnight, and drifting it to the
  // viewer's zone would render the PREVIOUS day west of Greenwich.
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export default LegalDocumentPage;
